import { ChevronDown16 } from '@carbon/icons-react';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsTeam, useOnClickOutside, useValueSetSelectorAvailable } from '@upright/ui/common/generic/hooks';
import { mobileOrNarrower } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { AboutUprightModal } from '@upright/ui/common/generic/modals/about-upright-modal';
import { KBUrl } from '@upright/ui/common/upright/kb-urls';
import { logout, setValuesSelectorOpen, setViewSettingsModalOpen } from '@upright/ui/untidy/redux/actions/actions';
import { getValueSetName, getValuesSelectorOpen, loggedInUser } from '@upright/ui/untidy/redux/selectors';
import { useMainContext } from '@upright/ui/views/root/main/context';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { navAnimateScaleIn, navBorderStyles, navFastTransition, navItemStyles } from '../shared';
import { DropdownItemWithIcon } from './dropdown-item-with-icon';
import ChatIcon from '@upright/ui/common/generic/components/icons/hero-icons/hi-chat-solid.svg';
import LinkIcon from '@upright/ui/common/generic/components/icons/hero-icons/hi-external-link-solid.svg';
import CogIcon from '@upright/ui/common/generic/components/icons/hero-icons/hi-settings-cog-solid.svg';
import SlidersIcon from '@streamlinehq/streamlinehq/img/streamline-mini-line/interface-essential/setting/interface-setting-slider-horizontal.svg';
import LogoutIcon from './icons/sl-logout-solid.svg';

// RN todo: refactor this to use Radix dropdown menu and align styles with release selector.

export const UserMenu = () => {
  const [open, setOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [, setContactModalOpen] = useMainContext().contactModal;
  const user = useSelector(loggedInUser);
  const isTeam = useIsTeam();
  const valueSetAvailable = useValueSetSelectorAvailable();
  const valueSet = useSelector(getValueSetName);
  const valuesSelectorOpen = useSelector(getValuesSelectorOpen);
  const btnRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([btnRef, dropdownRef], () => {
    setOpen(false);
  });

  function openContactModal() {
    setContactModalOpen(true);
  }

  function handleLogout() {
    dispatch(logout());
  }

  return (
    <Root>
      <DropdownButtonWrap
        ref={btnRef}
        onClick={() => {
          setOpen(!open);
        }}
        data-testid={E2eTestElementId.USER_DROPDOWN}
      >
        <NavText>{user?.firstName ? user.firstName : user?.username}</NavText>
        <DropdownIconWrap direction={open ? 'up' : 'down'}>
          <ChevronDown16 />
        </DropdownIconWrap>
      </DropdownButtonWrap>
      <AboutUprightModal
        isOpen={isAboutModalOpen}
        setIsOpen={setIsAboutModalOpen}
        onContactLinkPress={() => {
          setIsAboutModalOpen(false);
          setContactModalOpen(true);
        }}
      />
      {open && (
        <DropdownContent ref={dropdownRef} open={open}>
          <DropdownItemWithIcon icon={<CogIcon />} to={user && `/user/${user.username}/details`}>
            <Text>Account</Text>
          </DropdownItemWithIcon>
          {/* PLT-4824: team-only entry to per-feature view settings. The
              modal is mounted at the app root; this menu item just flips
              the redux flag. `Ctrl+*` and the status-bar item open the
              same modal. */}
          {isTeam && (
            <DropdownItemWithIcon
              icon={<CogIcon />}
              onClick={() => {
                setOpen(false);
                dispatch(setViewSettingsModalOpen(true));
              }}
            >
              <Text>View settings</Text>
            </DropdownItemWithIcon>
          )}
          {valueSetAvailable && (
            <DropdownItemWithIcon
              icon={<SlidersIcon />}
              onClick={() => {
                setOpen(false);
                dispatch(setValuesSelectorOpen(!valuesSelectorOpen));
              }}
            >
              <Text>Value set: {valueSet}</Text>
            </DropdownItemWithIcon>
          )}
          <DropdownItemWithIcon icon={<ChatIcon />} onClick={openContactModal}>
            <Text>Contact</Text>
          </DropdownItemWithIcon>
          <DropdownItemWithIcon icon={<LinkIcon />} href={KBUrl.INDEX}>
            <Text>Knowledge base</Text>
          </DropdownItemWithIcon>
          <DropdownItemWithIcon
            icon={<LinkIcon />}
            onClick={() => {
              setOpen(false);
              setIsAboutModalOpen(true);
            }}
          >
            <Text>About Upright Platform</Text>
          </DropdownItemWithIcon>
          <HorizontalDivider />
          <DropdownItemWithIcon icon={<LogoutIcon />} onClick={handleLogout} isLogOut={true}>
            <RedText>Log out</RedText>
          </DropdownItemWithIcon>
        </DropdownContent>
      )}
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownButtonWrap = styled.div`
  ${navItemStyles};
  display: flex;
  flex-shrink: 0;
  flex-grow: 0;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  * {
    overflow: visible;
  }
  ${mobileOrNarrower(css`
    align-items: flex-end;
  `)}
`;

const DropdownIconWrap = styled.div<{ direction: 'up' | 'down' }>`
  display: flex;
  transition: transform ${navFastTransition};
  transform: ${props => props.direction === 'up' && `rotate(180deg)`};
  overflow: visible;
`;

const NavText = styled.p`
  ${getTextStyle({ size: 'xxxs' })};
`;

const DropdownContent = styled.div<{ open: boolean }>`
  ${getTextStyle({ size: 'xxs' })};
  font-weight: 500;
  position: absolute;
  top: 40px;
  right: 20px;
  min-width: 200px;
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  ${navBorderStyles}
  ${navAnimateScaleIn()}
`;

const Text = styled.p`
  color: ${BouColor.PLATFORM_TEXT_WHITE};
`;

const RedText = styled.p`
  color: ${BouColor.TOOL_DANGER_RED};
`;

const HorizontalDivider = styled.div`
  margin: 3px 12px;
  height: 1px;
  background-color: ${BouColor.PLATFORM_BORDER_GRAY_DARK};
`;
